import { format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import type { DateRange, Matcher } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar, CalendarDayButton } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

function triggerLabel(value: DateRange | undefined): string {
  if (value?.from && value.to) {
    return `${format(value.from, 'MMM d, yyyy')} - ${format(value.to, 'MMM d, yyyy')}`;
  }
  return 'Any date';
}

/** Matches app mobile breakpoint (768px); narrow cap on small screens for grid layout. */
const dateFilterPillTriggerClassName = cn(
  'text-xs md:text-sm gap-2 rounded-full',
  'px-1 sm:px-2 md:px-3 md:has-data-[icon=inline-start]:pl-3',
  'max-w-26 sm:max-w-64',
  'h-6 sm:h-8 md:h-9',
);

export interface DateFilterButtonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: DateRange | undefined;
  onValueChange: (range: DateRange | undefined) => void;
  dottedDates?: Set<string>;
  disabled?: Matcher | Matcher[];
}

export function DateFilterButton({
  open,
  onOpenChange,
  value,
  onValueChange,
  dottedDates,
  disabled,
}: DateFilterButtonProps) {
  const label = useMemo(() => triggerLabel(value), [value]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={dateFilterPillTriggerClassName}
          >
            <span
              className="shrink-0 text-muted-foreground [&_svg]:size-4"
              data-icon="inline-start"
            >
              <CalendarIcon />
            </span>
            <span className="min-w-0 truncate text-foreground">{label}</span>
            <ChevronDown className="size-3 sm:size-4 shrink-0 opacity-50" aria-hidden />
          </Button>
        }
      />
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex flex-col gap-2 p-2">
          <Calendar
            mode="range"
            selected={value}
            defaultMonth={value?.from}
            numberOfMonths={2}
            onSelect={onValueChange}
            disabled={disabled}
            components={
              dottedDates && dottedDates.size > 0
                ? {
                    DayButton: ({ children, modifiers, day, ...props }) => {
                      const d = day.date;
                      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      const hasRegistered = !modifiers.outside && dottedDates.has(key);
                      return (
                        <CalendarDayButton day={day} modifiers={modifiers} {...props}>
                          {children}
                          <span className="flex h-1.5 items-center justify-center">
                            {hasRegistered && (
                              <span className="size-1.5 rounded-full bg-emerald-500" />
                            )}
                          </span>
                        </CalendarDayButton>
                      );
                    },
                  }
                : undefined
            }
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
